/* TAB 2 — Market Watch: full watchlist with sortable price/volume table + market bias card. */
import { useState } from "react";
import { C, Spark } from "./_shared";
import { usePrices, TickerSearch } from "./_prices";
import TickerLink from "./TickerLink";
import { useCompanyNames } from "./_companyNames";

// ── Session-3 redesign tokens ──
const T = {
  card:       { background:"#ffffff", border:"1px solid #e2e8f0", borderRadius:8, padding:"14px 18px", boxShadow:"0 1px 3px rgba(0,0,0,0.06)" },
  sectionLab: { fontSize:10, fontWeight:500, color:"#94a3b8", textTransform:"uppercase", letterSpacing:"0.06em" },
  subTab:     (active) => ({
    background: active ? "#f1f5f9" : "transparent",
    color:      active ? "#0f172a" : "#64748b",
    fontSize:12, fontWeight:500, padding:"6px 14px", borderRadius:6,
    border:"none", cursor:"pointer",
  }),
};

export default function MarketWatchTab({ requestAnalyse, requestLogTrade }) {
  const { prices, watchlist, addTicker, removeTicker, refresh, loading } = usePrices();
  const [sortBy,    setSortBy]    = useState("chgPct");
  const [filter,    setFilter]    = useState("All");

  const spy = prices["SPY"], qqq = prices["QQQ"], vix = prices["VIX"];

  const rows = watchlist
    .filter(t => t !== "VIX")
    .map(t => prices[t])
    // Only render rows where the price payload is fully valid — otherwise
    // a partial/null payload would crash the toFixed() calls below.
    .filter(d => d && d.ok && typeof d.price === "number" && Number.isFinite(d.price)
                  && typeof d.chg === "number" && Number.isFinite(d.chg)
                  && typeof d.chgPct === "number" && Number.isFinite(d.chgPct))
    .filter(d => {
      if (filter==="Gainers") return d.chgPct > 0;
      if (filter==="Losers")  return d.chgPct < 0;
      if (filter==="Active")  return d.vol > 5e6;
      return true;
    })
    .sort((a,b) => {
      if (sortBy==="chgPct") return b.chgPct - a.chgPct;
      if (sortBy==="vol")    return b.vol - a.vol;
      if (sortBy==="price")  return b.price - a.price;
      if (sortBy==="range")  return (b.high-b.low)/b.prev - (a.high-a.low)/a.prev;
      return 0;
    });

  const addNew = (item) => { if (item?.symbol) addTicker(item.symbol); };
  const { name: companyName } = useCompanyNames();

  // Bullish-bias signal: VIXY < 25 ≈ spot VIX < 17 (calm). The literal
  // "VIX < 18" rule was meaningless when the proxy is VIXY.
  const vixCalmThreshold = vix?._proxy ? 25 : 18;
  const bullSignals = [spy?.chg>0, qqq?.chg>0, (vix?.price??99) < vixCalmThreshold, rows.filter(r=>r.chgPct>0).length > rows.length*0.6].filter(Boolean).length;
  const bias = bullSignals >= 3 ? { label:"Bullish Bias", color:C.green, desc:"Market trending up — long setups preferred" }
             : bullSignals >= 2 ? { label:"Neutral",      color:C.amber, desc:"Mixed signals — trade selectively, reduce size" }
             :                    { label:"Bearish Bias",  color:C.red,   desc:"Market under pressure — avoid longs or sit out" };

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:12 }}>

      {/* Search bar card */}
      <div style={{ background:"#fff", border:"1px solid #e2e8f0", borderRadius:8, padding:"12px 16px", boxShadow:"0 1px 3px rgba(0,0,0,0.06)", display:"flex", alignItems:"center", gap:12, flexWrap:"wrap" }}>
        <span style={{ fontSize:16, color:"#94a3b8" }}>🔎</span>
        <div style={{ flex:1, minWidth:240 }}>
          <TickerSearch onSelect={addNew} placeholder='Search any US stock — type "Apple" or "AAPL"…' />
        </div>
        <span style={{ fontSize:11, color:"#94a3b8" }}>{watchlist.length} on watchlist</span>
      </div>

      {/* Index cards — top border colour by direction */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))", gap:10 }}>
        {[
          { label:"S&P 500 (SPY)", d:spy },
          { label:"NASDAQ (QQQ)",  d:qqq },
          { label:"VIX Fear Index",d:vix, isVix:true },
        ].map(({label,d,isVix}) => {
          const topAccent = d?.chg == null ? "#94a3b8" : d.chg >= 0 ? "#16a34a" : "#dc2626";
          return (
            <div key={label} style={{ ...T.card, padding:"14px 18px", borderTop:`3px solid ${topAccent}` }}>
              <div style={{ ...T.sectionLab, marginBottom:6 }}>
                {label}
                {isVix && d?._proxy && (
                  <span title={`Approximate via ${d._proxy} ETF — not the actual VIX index. Spot VIX ≈ ${(d.price/1.55).toFixed(1)}.`} style={{ fontSize:9, color:"#cbd5e1", marginLeft:4 }}>
                    (~{d._proxy})
                  </span>
                )}
              </div>
              {d?.ok ? (
                <>
                  {isVix && d._proxy ? (
                    <>
                      <div style={{ fontSize:24, fontFamily:"monospace", fontWeight:700, color:d.chg>=0?"#dc2626":"#16a34a" }}>≈{(d.price/1.55).toFixed(1)}</div>
                      <div style={{ fontSize:11, color:"#94a3b8", marginBottom:2 }}>VIXY ${d.price.toFixed(2)} ({d.chg>=0?"+":""}{d.chgPct.toFixed(2)}%)</div>
                    </>
                  ) : (
                    <>
                      <div style={{ fontSize:24, fontFamily:"monospace", fontWeight:700, color:"#0f172a" }}>${d.price.toFixed(2)}</div>
                      <div style={{ fontSize:13, fontFamily:"monospace", color:d.chg>=0?"#16a34a":"#dc2626", marginBottom:4 }}>{d.chg>=0?"+":""}{d.chg.toFixed(2)} ({d.chgPct.toFixed(2)}%)</div>
                    </>
                  )}
                  <Spark data={d.closes} color={d.chg>=0?"#16a34a":"#dc2626"} w={120} h={28}/>
                </>
              ) : <div style={{ fontSize:13, color:"#cbd5e1", padding:"8px 0" }}>Loading···</div>}
            </div>
          );
        })}
        {/* Market bias card */}
        {(() => {
          const biasColor = bias.color === C.green ? "#16a34a" : bias.color === C.red ? "#dc2626" : "#d97706";
          return (
            <div style={{ ...T.card, padding:"14px 18px", borderTop:`3px solid ${biasColor}` }}>
              <div style={{ ...T.sectionLab, marginBottom:6 }}>Market bias</div>
              <div style={{ fontSize:14, fontWeight:600, color:biasColor }}>{bias.label}</div>
              <div style={{ fontSize:11, color:"#94a3b8", marginTop:4, lineHeight:1.5 }}>{bias.desc}</div>
            </div>
          );
        })()}
      </div>

      {/* Filter pills + sort dropdown row */}
      <div style={{ display:"flex", gap:8, flexWrap:"wrap", alignItems:"center" }}>
        <div style={{ display:"flex", gap:4 }}>
          {["All","Gainers","Losers","Active"].map(f => (
            <button key={f} onClick={()=>setFilter(f)} style={T.subTab(filter===f)}>{f}</button>
          ))}
        </div>
        <select value={sortBy} onChange={e=>setSortBy(e.target.value)} style={{ padding:"7px 10px", borderRadius:6, border:"1px solid #d1d5db", fontSize:12, background:"#fff", width:140 }}>
          <option value="chgPct">Sort: % Change</option>
          <option value="vol">Sort: Volume</option>
          <option value="price">Sort: Price</option>
          <option value="range">Sort: Day Range</option>
        </select>
        <div style={{ display:"flex", gap:6, marginLeft:"auto" }}>
          <button onClick={()=>refresh()} disabled={loading} style={{ padding:"6px 14px", borderRadius:6, background:"#fff", color:"#64748b", border:"1px solid #e2e8f0", fontSize:12, cursor:"pointer" }}>{loading?"···":"↻ Refresh"}</button>
        </div>
      </div>

      {/* Watchlist table card */}
      <div style={{ background:"#fff", border:"1px solid #e2e8f0", borderRadius:8, overflow:"hidden", boxShadow:"0 1px 3px rgba(0,0,0,0.06)" }}>
        <table style={{ width:"100%", borderCollapse:"collapse" }}>
          <thead>
            <tr style={{ background:"#f8fafc", borderBottom:"1px solid #e2e8f0" }}>
              {["Ticker","Price","Change","% Chg","Open","High","Low","Prev Close","Volume","Range %","Trend",""].map(h => (
                <th key={h} style={{ padding:"8px 12px", textAlign:"left", color:"#94a3b8", fontWeight:500, fontSize:10, textTransform:"uppercase", letterSpacing:"0.06em", whiteSpace:"nowrap" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(d => {
              const up = d.chg >= 0;
              const rangePct = d.prev ? ((d.high-d.low)/d.prev*100) : 0;
              return (
                <tr key={d.ticker}
                  style={{ borderBottom:"1px solid #f1f5f9", fontSize:12 }}
                  onMouseEnter={(e) => e.currentTarget.style.background = "#f8fafc"}
                  onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                >
                  <td style={{ padding:"10px 12px" }}>
                    <div style={{ display:"flex", flexDirection:"column" }}>
                      <span style={{ fontFamily:"monospace", fontWeight:600, color:"#4f46e5", fontSize:13 }}>
                        <TickerLink ticker={d.ticker} name={companyName(d.ticker)} onAnalyse={requestAnalyse} onLogTrade={requestLogTrade}/>
                      </span>
                      <span style={{ fontSize:11, color:"#94a3b8", marginTop:1, maxWidth:200, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                        {companyName(d.ticker, "")}
                      </span>
                    </div>
                  </td>
                  <td style={{ padding:"10px 12px", fontFamily:"monospace", fontWeight:600, color:"#0f172a" }}>${d.price.toFixed(2)}</td>
                  <td style={{ padding:"10px 12px", fontFamily:"monospace", color:up?"#16a34a":"#dc2626", fontWeight:600 }}>{up?"+":""}{d.chg.toFixed(2)}</td>
                  <td style={{ padding:"10px 12px" }}>
                    <span style={{ fontFamily:"monospace", fontWeight:700, color:up?"#16a34a":"#dc2626", background:up?"#dcfce7":"#fee2e2", padding:"2px 6px", borderRadius:4, fontSize:11 }}>{up?"+":""}{d.chgPct.toFixed(2)}%</span>
                  </td>
                  <td style={{ padding:"10px 12px", fontFamily:"monospace", color:"#64748b" }}>${d.open.toFixed(2)}</td>
                  <td style={{ padding:"10px 12px", fontFamily:"monospace", color:"#64748b" }}>${d.high.toFixed(2)}</td>
                  <td style={{ padding:"10px 12px", fontFamily:"monospace", color:"#64748b" }}>${d.low.toFixed(2)}</td>
                  <td style={{ padding:"10px 12px", fontFamily:"monospace", color:"#64748b" }}>${d.prev.toFixed(2)}</td>
                  <td style={{ padding:"10px 12px", fontFamily:"monospace", color:"#64748b" }}>{d.vol>=1e6?`${(d.vol/1e6).toFixed(1)}M`:`${(d.vol/1e3).toFixed(0)}K`}</td>
                  <td style={{ padding:"10px 12px", fontFamily:"monospace", color:"#d97706", fontWeight:rangePct>2?600:400 }}>{rangePct.toFixed(2)}%</td>
                  <td style={{ padding:"10px 12px" }}><Spark data={d.closes} color={up?"#16a34a":"#dc2626"} w={60} h={20}/></td>
                  <td style={{ padding:"10px 12px", textAlign:"right" }}>
                    <button
                      onClick={() => removeTicker(d.ticker)}
                      title={`Remove ${d.ticker} from watchlist`}
                      data-testid={`remove-ticker-${d.ticker}`}
                      style={{ width:22, height:22, borderRadius:5, border:"none", background:"transparent", color:"#94a3b8", cursor:"pointer", fontSize:14, lineHeight:1, opacity:0.4, transition:"opacity 0.15s" }}
                      onMouseEnter={(e) => e.currentTarget.style.opacity = 1}
                      onMouseLeave={(e) => e.currentTarget.style.opacity = 0.4}
                    >×</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {rows.length===0 && (
          <div data-testid="market-watch-empty" style={{ padding:"48px 24px", textAlign:"center" }}>
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#e2e8f0" strokeWidth="1.5" style={{ margin:"0 auto", display:"block" }}>
              <line x1="3" y1="20" x2="21" y2="20"/>
              <polyline points="3 17 9 11 13 15 21 7"/>
            </svg>
            <div style={{ fontSize:14, color:"#94a3b8", marginTop:12 }}>Your watchlist is empty</div>
            <div style={{ fontSize:12, color:"#cbd5e1", marginTop:4 }}>Search for a stock above to add it to your watchlist</div>
          </div>
        )}
      </div>
    </div>
  );
}
